import React, {FC, useContext, useEffect, useMemo, useState} from "react";
import {useHistory} from "react-router-dom";
import {EntitySelect} from "../common/inputs/EntitySelect";
import {BYTable} from "../common/views/BYTable";
import {
    useAddUserToOperation,
    useRemoveUserFromOperation,
    useThrottledLayoutEffect
} from "../api/hooks";
import {User} from "../common/types/Types";
import {BYInput} from "../common/inputs/BYInput";
import {AuthContext} from "../providers/AuthProvider";
import BYAsyncButton from "../common/buttons/BYAsyncButton";
import {useAlert} from "react-alert";

interface UserListProps {
    users: User[];
    isLoading: boolean;
    onActiveChange?: (arg0: boolean) => void;
    activeFilter?: boolean
    operationId?: number;
}

export const UserList: FC<UserListProps> = (props) => {
    const {users, isLoading, onActiveChange, operationId} = props;
    const {formatter, canCreateOperationUsers} = useContext(AuthContext);

    const [filteredUsers, setFilteredUsers] = useState<User[]>(users);
    const [searchText, setSearchText] = useState('');
   /*  const [activeFilter, setActiveFilter] = useState(true); */

    const history = useHistory();
    const {mutateAsync: removeUserFromOperation} = useRemoveUserFromOperation();
    const alert = useAlert();

    useEffect(() => {
        setFilteredUsers(users);
    }, [users]);

    const canCreateUsers = canCreateOperationUsers(operationId);

    useThrottledLayoutEffect(() => {
        const filteredUsers = users.filter((user) => {
            const code = `${user.prenom} ${user.nom} ${user.email}`.toUpperCase();
            const reference = `${user.refUti} ${user.nomEntreprise} ${user.login}`.toUpperCase();
            const city = `${user.adresse} ${user.ville}`.toUpperCase();
            const searchTextUpperCased = searchText.toUpperCase();

            return code.includes(searchTextUpperCased) || reference.includes(searchTextUpperCased) || city.includes(searchTextUpperCased);
        });

        setFilteredUsers(filteredUsers);
    }, 300, [searchText, users]);

    const columns = ['login', 'nom', 'email', 'nomEntreprise'] as (keyof User)[];
    const dataTransform = {
        'nom': (user) => <b>{user.nom} {user.prenom}</b>,
        'aPourRoles': (user) => {
            return formatter.getRoleDescription(user.aPourRoles.find(a => a.operationRefOperation === operationId)?.roleRefRole)
        },
        'indicActif': (user) => {
            return <div className="text-center">
                <BYAsyncButton
                    className="btn btn-danger btn-sm flex-grow-1" type="button" onClick={async () => {
                        try {
                            await removeUserFromOperation({
                                operationId,
                                aPourRoleId: user.aPourRoles[0]?.refAPourRole
                            });
                            alert.success(`Utilisateur de l'opération mis à jour`);
                        } catch (e) {
                            console.error(e);
                            alert.error(`Erreur lors de la mise à jour de l'utilisateur`);
                        }
                }}
                ><i className="fa fa-minus"/>
                </BYAsyncButton>
            </div>;
        }
    };
    if (operationId) {
        columns.push('aPourRoles');
        if (canCreateUsers) {
            columns.push('indicActif');
        }
    }

    const AddUserView = () => {
        const [roleId, setRoleId] = useState();
        const [userId, setUserId] = useState();
        const {mutateAsync: addUserToOperation, isLoading} = useAddUserToOperation();

        return <div className="col-8 row">
            <div className="col-5">
                <EntitySelect
                    title="Ajouter utilisateur à opération"
                    placeholder="Choisir role"
                    value={roleId}
                    type="role"
                    className="w-100"
                    onChange={(newValue) => setRoleId(newValue)}
                />
            </div>
            <div className="col-5">
                <EntitySelect
                    placeholder="Choisir utilisateur"
                    value={userId}
                    type="available-user"
                    operationId={operationId}
                    className="w-100 mt-3"
                    onChange={(newValue) => setUserId(newValue)}
                />
            </div>
            <div className="col-2">
                <button
                    disabled={!userId || !roleId || isLoading}
                    type="button"
                    onClick={async () => {
                        try {
                            await addUserToOperation({userId, roleId, operationId});
                            setRoleId(undefined);
                            setUserId(undefined);
                            alert.success(`Utilisateur ajouté à l'opération`);
                        } catch (e) {
                            console.error(e);
                            alert.error(`Erreur lors de la mise à jour de l'utilisateur`);
                        }
                    }}
                    className="btn btn-warning mt-3 w-100">Ajouter</button>
            </div>
        </div>;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const AddUserViewMemoized = useMemo(() => <AddUserView />, [users]);

    return (
        <div>
            <div className="row mb-3">
                <div className={operationId === undefined ? 'col-9' : 'col-4'}>
                    <BYInput type="text"
                             title="Rechercher"
                             placeholder="Identifiant, nom, prénom, email, adresse, ville, entreprise, etc."
                             className="form-control"
                             value={searchText}
                             onChange={(e) => setSearchText(e.target.value)}/>
                </div>
                {operationId === undefined && <div className="col-3">
                    <EntitySelect
                        title={'Active'}
                        type="boolean"
                        onChange={(value) => {
                            if (onActiveChange) {
                                onActiveChange(value);
                            }
                        }}
                        value={props.activeFilter}
                    />
                </div>}
                {operationId !== undefined && canCreateOperationUsers(operationId) && AddUserViewMemoized}
            </div>

            <BYTable
                rowsPerPage={20}
                loading={isLoading}
                data={filteredUsers}
                columns={columns}
                columnNames={['Identifiant', 'Nom', 'Email', 'Entreprise', 'Role']}
                dataTransform={dataTransform}
                onClick={!operationId ? (user) => {
                    history.push(`/user/${user.refUti}`);
                } : undefined}
                keyExtractor={(user) => `user-list-${user.aPourRoles?.length ? user.aPourRoles[0].refAPourRole : user.refUti}`}
            />
        </div>
    );
};
